import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardText,
  CardTitle,
  Col,
  FormGroup,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Row,
} from 'reactstrap';
import { EmailTemplateInfo, EmailTemplateSchema } from '../../../../../shared/email';
import { ActionButtonModal, MaterialIcon } from '../../../../components';
import { useTranslation } from '../../../../translations';
import { Fetcher, useFetcher, useForm } from '../../../../utils';
import { APIError } from '../../../../utils/APIClient';
import { debounce } from '../../../../utils/debounce';
import { DefaultTemplateCard, RenderContainer, TemplateOverrideCard } from './TemplateBodyEntry';

export const TemplateEditor: FC<RouteComponentProps<{ templateName: string }>> = ({ match }) => {
  const { templateName } = match.params;

  const [isModifying, setIsModifying] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [creationIsOrgWide, setCreationIsOrgWide] = useState<boolean | undefined>(undefined);

  const providedTemplate = useFetcher(async () => {
    return await api.getTemplate(templateName);
  }, [templateName]);

  if (!providedTemplate.success) {
    return <Fetcher result={providedTemplate} />;
  }

  return (
    <Row>
      <Col lg={12}>
        <h1>{templateName}</h1>
        <p>{providedTemplate.data.description}</p>
        <hr />
      </Col>
      <>
        {isModifying && creationIsOrgWide !== undefined && (
          <ModifyTemplate
            close={() => {
              setIsModifying(false);
            }}
            data={providedTemplate.data}
            isCreating={isCreating}
            isOrgWide={creationIsOrgWide}
            refresh={providedTemplate.refresh}
          />
        )}
        {!isModifying && (
          <Col lg={12} xs={12}>
            <DefaultTemplateCard
              active={providedTemplate.data.active === 'default'}
              body={providedTemplate.data.defaultBody}
            />
            <TemplateOverrideCard
              active={providedTemplate.data.active === 'convention'}
              enableModify={() => {
                setCreationIsOrgWide(false);
                setIsModifying(true);
              }}
              level="convention"
              override={providedTemplate.data.conventionOverride}
              refresh={providedTemplate.refresh}
              setIsCreating={setIsCreating}
              templateName={providedTemplate.data.name}
            />
            <TemplateOverrideCard
              active={providedTemplate.data.active === 'organization'}
              enableModify={() => {
                setCreationIsOrgWide(true);
                setIsModifying(true);
              }}
              level="organization"
              override={providedTemplate.data.organizationOverride}
              refresh={providedTemplate.refresh}
              setIsCreating={setIsCreating}
              templateName={providedTemplate.data.name}
            />
          </Col>
        )}
      </>
    </Row>
  );
};

const ModifyTemplate: FC<{
  readonly isOrgWide: boolean;
  readonly data: EmailTemplateInfo;
  readonly isCreating: boolean;
  readonly close: () => void;
  readonly refresh: () => void;
}> = ({ data, isOrgWide, isCreating, close, refresh }) => {
  const { ts } = useTranslation();
  const [changePending, setChangePending] = useState(true);
  const inputBoxRef = useRef<HTMLInputElement>(null);
  const defaultValue = isCreating
    ? data.defaultBody.raw
    : isOrgWide
      ? data.organizationOverride!.body.raw
      : data.conventionOverride!.body.raw;

  const defaultSubject = isCreating
    ? ''
    : isOrgWide
      ? data.organizationOverride?.subject
      : data.conventionOverride?.subject;

  const [previewBody, setPreviewBody] = useState(defaultValue);
  const [subject, setSubject] = useState(defaultSubject || '');

  const debounceBody = useMemo(
    () =>
      debounce((newBody: string) => {
        setPreviewBody(newBody);
      }, 1000),
    [],
  );

  const bodyFetch = useFetcher(async () => {
    if (previewBody.length === 0) {
      return;
    }

    const result = await api.previewTemplate(data.name, previewBody);
    setChangePending(false);
    return result;
  }, [data.name, previewBody]);

  const attemptRender = useCallback(() => {
    setChangePending(true);
    debounceBody(inputBoxRef.current?.value ?? '');
  }, [debounceBody]);

  const form = useForm(async () => {
    await api.modifyOverride(
      data.name,
      previewBody,
      subject.length === 0 ? null : subject,
      isOrgWide ? 'organization' : 'convention',
    );

    toast.success(ts(isCreating ? 'new_override_has_been_created' : 'override_has_been_modified'));

    close();
    refresh();
  }, [data.name, previewBody, subject, isOrgWide, ts, isCreating, close, refresh]);

  const variableKeys = data.schema.keys ?? {};

  return (
    <>
      <Col lg={8} xs={12}>
        <Card className="mb-10">
          <CardBody>
            <CardTitle className="align-middle">
              {ts(isCreating ? 'new' : 'modifying')} {ts(isOrgWide ? 'organization' : 'convention')}{' '}
              {ts('level')} {ts('override')}
              {!bodyFetch.loading && (
                <span className="align-middle float-end badge-list compact">
                  {bodyFetch.error && <Badge color="danger">{ts('parse_error')}</Badge>}
                  {bodyFetch.data?.variableErrors.map((variable) => {
                    return (
                      <Badge color="danger" key={variable}>
                        {ts('variable_is_invalid', variable)}
                      </Badge>
                    );
                  })}
                </span>
              )}
              <div className="float-end">
                {isCreating && !data.required && (
                  <ActionButtonModal
                    actionContent="common.disable"
                    buttonContent="disable_template"
                    color="danger"
                    id="disable"
                    onComplete={async () => {
                      await api.modifyOverride(
                        data.name,
                        previewBody,
                        subject,
                        isOrgWide ? 'organization' : 'convention',
                        true,
                      );

                      toast.success(ts('new_override_has_been_created'));

                      close();
                      refresh();
                    }}
                    title="disable_email_template"
                  >
                    {ts('this_action_will_prevent_the', data.name)}
                  </ActionButtonModal>
                )}
              </div>
            </CardTitle>
            <form onSubmit={form.onSubmit}>
              <FormGroup>
                <Label for="subject">{ts('subject_override_optional')}</Label>
                <Input
                  id="subject"
                  name="subject"
                  onChange={(e) => {
                    setSubject(e.target.value);
                  }}
                  placeholder={data.subject}
                  value={subject}
                />
              </FormGroup>
              <FormGroup>
                <Input
                  defaultValue={defaultValue}
                  id="body"
                  innerRef={inputBoxRef}
                  name="body"
                  onChange={attemptRender}
                  style={{ height: '250px' }}
                  type="textarea"
                />
              </FormGroup>
              <FormGroup>
                <div className="float-end">
                  <Button color="secondary" onClick={close}>
                    {ts('common.cancel')}
                  </Button>{' '}
                  <Button
                    color="primary"
                    disabled={
                      form.saving ||
                      changePending ||
                      bodyFetch.data?.variableErrors.length !== 0 ||
                      bodyFetch.error
                        ? true
                        : undefined
                    }
                    type="submit"
                  >
                    {ts('submit')}
                  </Button>
                </div>
              </FormGroup>
            </form>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <CardTitle>{ts('preview')}</CardTitle>
            <hr />
            {bodyFetch.data === undefined && <Fetcher result={bodyFetch} />}
            {bodyFetch.error instanceof APIError && (
              <Card className="danger">
                <CardBody className="text-center">
                  <MaterialIcon large name="sync_problem" type="danger" />
                  <CardTitle>{ts('template_was_unable_to_be')}</CardTitle>
                  <CardText tag="div">
                    <p>{ts('a_parsing_error_occurred_view')}</p>
                    <hr />
                    <pre className="text-start">
                      {bodyFetch.error.apiResponse.errors.logic?.map((e) => e.args?.['internal'])}
                    </pre>
                  </CardText>
                </CardBody>
              </Card>
            )}
            {bodyFetch.data && <RenderContainer data={bodyFetch.data.preview} />}
          </CardBody>
        </Card>
      </Col>
      <Col lg={4} xs={12}>
        <Card>
          <CardBody>
            <CardTitle>
              {ts('available_variables')}
              <a
                href="https://concat.app/docs/guides/templates#variables"
                rel="noreferrer"
                target="_blank"
              >
                <span className="inline-icon" id="help-variabledocs">
                  <MaterialIcon name="help" small />
                </span>
              </a>
            </CardTitle>
            <ListGroup flush>
              {Object.entries(variableKeys).map(([key, value]) => {
                switch (value.type) {
                  case 'object': {
                    return <ObjectVariable key={key} schema={value} variable={key} />;
                  }

                  case 'array': {
                    return (
                      <ArrayVariable
                        key={key}
                        sample={value.sample}
                        schema={value}
                        variable={key}
                      />
                    );
                  }

                  default: {
                    return <StaticVariable key={key} sample={value.sample} variable={key} />;
                  }
                }
              })}
            </ListGroup>
          </CardBody>
        </Card>
      </Col>
    </>
  );
};

const StaticVariable: FC<{
  readonly variable: string;
  readonly sample?: boolean | number | string | Date | null;
}> = ({ variable, sample }) => {
  const { ts } = useTranslation();
  if (sample === undefined) {
    return;
  }

  let parsedSample: number | string | Date;

  // if you directly insert a boolean value, react renders nothing
  if (typeof sample === 'boolean') {
    parsedSample = sample ? 'True' : 'False';
  } else {
    parsedSample = sample ?? 'Null';
  }

  return (
    <ListGroupItem style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
      {variable}{' '}
      <small className="text-muted">
        {ts('common.example')}: {parsedSample.toString()}
      </small>
    </ListGroupItem>
  );
};

const ArrayVariable: FC<{
  readonly variable: string;
  readonly schema: EmailTemplateSchema;
  readonly sample?: boolean | number | string | Date;
}> = ({ variable, schema, sample }) => {
  const { ts } = useTranslation();
  if (schema.itemType === 'object') {
    if (!schema.keys) {
      return ts('common.unknown');
    }

    return (
      <ListGroupItem style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
        <h5>
          {variable}: <small className="text-muted">{ts('complex_list')}</small>
          <MaterialIcon name="keyboard_arrow_down" />
        </h5>
        <ListGroup>
          {Object.entries(schema.keys).map(([key, value]) => {
            switch (value.type) {
              case 'object': {
                return <ObjectVariable key={key} schema={value} variable={key} />;
              }

              case 'array': {
                return <ArrayVariable key={key} schema={value} variable={key} />;
              }

              default: {
                return <StaticVariable key={key} sample={value.sample} variable={key} />;
              }
            }
          })}
        </ListGroup>
      </ListGroupItem>
    );
  }

  return (
    <ListGroupItem>
      <span style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {variable}: <small className="text-muted">list, ex: {sample?.toString()}</small>
      </span>
    </ListGroupItem>
  );
};

const ObjectVariable: FC<{ readonly variable: string; readonly schema: EmailTemplateSchema }> = ({
  variable,
  schema,
}) => {
  return (
    <ListGroupItem>
      <h5>
        {variable} <MaterialIcon name="keyboard_arrow_down" />
      </h5>
      <ListGroup flush>
        {Object.entries(schema.keys!).map(([key, value]) => {
          switch (value.type) {
            case 'object': {
              return <ObjectVariable key={key} schema={value} variable={key} />;
            }

            case 'array': {
              return (
                <ArrayVariable key={key} sample={value.sample} schema={value} variable={key} />
              );
            }

            default: {
              return <StaticVariable key={key} sample={value.sample} variable={key} />;
            }
          }
        })}
      </ListGroup>
    </ListGroupItem>
  );
};
